import { CSSProperties } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import {
  MarginTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { AssetResponse } from "../../../utils/asset";
import { IInputProps } from "../form";
import { IPerpsMarket } from "../../../contexts/MarketInstrumentContext/useGetMarkets";

export enum PerpFormFieldKeyEnum {
  PRICE = "price",
  AMOUNT = "amount",
  LEVERAGE = "leverage",
  TRIGGER_PRICE = "triggerPrice",
  MARGIN_TYPE = "marginType",
}

export type IPerpsFormFieldValues = {
  [key in PerpFormFieldKeyEnum]: string;
};

export const usePerpsForm = () =>
  useForm<IPerpsFormFieldValues>({
    mode: "onChange",
    reValidateMode: "onChange",
  });

export interface IPriceInputProps extends IInputProps {
  minPrice: number;
  placeholder?: string;
  isStopOrder?: boolean;
  rightAccessory?: JSX.Element;
}

export interface ITriggerPriceInputProps extends IPriceInputProps {
  markPrice?: string;
}

export interface IExtraTriggerPriceInputProps extends ITriggerPriceInputProps {
  markPrice?: string;
  leftAccessory?: JSX.Element;
  resetInput: () => void;
}

export interface IOrderSizeInputProps extends IInputProps {
  style?: CSSProperties;
  selectedAsset?: AssetResponse;
  onChangeAsset: (asset?: AssetResponse) => void;
  minAmountSize: number;
  underlyingAsset?: AssetResponse;
  existingPositionSize?: string;
  existingOrderInfo?: {
    newOrderSizeLimit: string;
    side: SideResponse;
    size: string;
  };
  placeholder?: string;
  showValidationError?: boolean;
  /**
   * What a failed balance check ran out of. A perp sizes against buying power;
   * a spot BUY spends the quote token, a spot SELL sells the base token.
   * Defaults to buying power (perp, edit-order).
   */
  insufficientKind?: OrderSizeInsufficientKind;
  /** The token named by a spot message ("Insufficient USDC balance"). */
  insufficientAsset?: string;
}

export type OrderSizeInsufficientKind = "buying_power" | "quote_balance" | "base_balance";

export interface IOrderSizeSliderProps extends Omit<IInputProps, "register"> {
  leverageColor: string;
  /** 0..100 — % of max buying power used by the current order. */
  sizePercent: number;
  /** Receives 0..100 when the user drags the slider. */
  onChangeSizePercent: (percent: number) => void;
}

export interface IMarginSelectorProps {
  form: UseFormReturn<IPerpsFormFieldValues, any>;
  side: SideResponse;
  instrument?: IPerpsMarket;
  /** When true, renders as 3 compact top-bar buttons instead of the full widget */
  renderAsTopBar?: boolean;
}

export interface IMarginIconProps {
  marginType: MarginTypeResponse;
  isSelected: boolean;
}
